import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './VariantForgeModal.css';
import {
  DEFAULT_FORGE_CONFIG,
  MAX_FORGE_VARIANTS,
  normalizeForgeConfig,
  countTemplateWords,
  countTemplateInstructions,
} from './variantForge';

const LABELS = {
  German: {
    title: 'Ähnliche Variante erzeugen',
    subtitle: 'Die KI leitet neue Varianten von dieser einen ab — statt bei null anzufangen.',
    sourceLabel: 'Vorbild',
    words: 'Wörter',
    instructions: 'KI-Prompts',
    similarity: 'Wie ähnlich soll sie sein?',
    similarityHint: 'Bezieht sich auf den sichtbaren E-Mail-Text.',
    twin: 'Zwilling',
    twinDesc: 'Gleicher Aufbau, gleiche Länge, nur andere Formulierungen',
    sibling: 'Geschwister',
    siblingDesc: 'Gleiches Gerüst und alle Argumente, aber komplett neu formuliert',
    cousin: 'Cousin',
    cousinDesc: 'Gleiches Ziel, neuer Weg — anderer Hook, andere Reihenfolge',
    brace: 'Was passiert mit den {KI-Prompts}?',
    braceHint: 'Die Anweisungen in einfachen Klammern, die pro Lead aufgelöst werden.',
    verbatim: '1:1 übernehmen',
    verbatimDesc: 'Zeichengenau kopiert. Nur der Text drumherum wird neu.',
    adapt: 'Leicht anpassen',
    adaptDesc: 'Gleiche Datenquelle, gleiches Ergebnis — aber neu formuliert',
    rethink: 'Neu denken',
    rethinkDesc: 'Darf umgebaut werden: andere Datenquellen, neue Prompts',
    braceEmpty: 'Dieses Vorbild enthält keine {KI-Prompts} — diese Einstellung greift erst, wenn welche drin sind.',
    length: 'Länge',
    lengthMatch: 'Gleich lassen',
    lengthShorter: 'Kürzer',
    lengthLonger: 'Länger',
    keepFormatting: 'Formatierung übernehmen',
    keepFormattingHint: 'Gleiche Absätze und Zeilenumbrüche wie im Original',
    count: 'Anzahl',
    note: 'Was soll anders sein?',
    noteOptional: 'optional',
    notePlaceholder: 'z.B. härterer CTA, stärker auf Zeitdruck, ohne die Frage am Ende...',
    noteHint: 'Überschreibt die Ähnlichkeitsstufe, wo sich beides widerspricht.',
    costEstimate: 'Geschätzte Kosten',
    credits: 'Credits',
    cancel: 'Abbrechen',
    generateOne: 'Variante erzeugen',
    generateMany: 'Varianten erzeugen',
  },
  English: {
    title: 'Derive a similar variant',
    subtitle: 'The AI builds new variants from this one instead of starting from scratch.',
    sourceLabel: 'Blueprint',
    words: 'words',
    instructions: 'AI prompts',
    similarity: 'How similar should it be?',
    similarityHint: 'Applies to the visible email copy.',
    twin: 'Twin',
    twinDesc: 'Same structure, same length, only the wording changes',
    sibling: 'Sibling',
    siblingDesc: 'Same skeleton and every argument, but written fresh',
    cousin: 'Cousin',
    cousinDesc: 'Same goal, new route — new hook, different order',
    brace: 'What happens to the {AI prompts}?',
    braceHint: 'The single-brace instructions that get resolved per lead.',
    verbatim: 'Keep 1:1',
    verbatimDesc: 'Copied character-for-character. Only the copy around them changes.',
    adapt: 'Adapt lightly',
    adaptDesc: 'Same data source, same output — but rephrased',
    rethink: 'Rethink',
    rethinkDesc: 'May be redesigned: other data sources, new prompts',
    braceEmpty: 'This blueprint has no {AI prompts}, so this setting only matters once it does.',
    length: 'Length',
    lengthMatch: 'Keep it',
    lengthShorter: 'Shorter',
    lengthLonger: 'Longer',
    keepFormatting: 'Keep the formatting',
    keepFormattingHint: 'Same paragraphs and line breaks as the original',
    count: 'Variants',
    note: 'What should be different?',
    noteOptional: 'optional',
    notePlaceholder: 'e.g. harder CTA, lean on urgency, drop the closing question...',
    noteHint: 'Overrides the similarity level wherever the two conflict.',
    costEstimate: 'Estimated cost',
    credits: 'credits',
    cancel: 'Cancel',
    generateOne: 'Generate variant',
    generateMany: 'Generate variants',
  },
};

const SIMILARITY_OPTIONS = [
  { value: 'twin', icon: 'fa-clone', labelKey: 'twin', descKey: 'twinDesc' },
  { value: 'sibling', icon: 'fa-code-branch', labelKey: 'sibling', descKey: 'siblingDesc' },
  { value: 'cousin', icon: 'fa-random', labelKey: 'cousin', descKey: 'cousinDesc' },
];

const BRACE_OPTIONS = [
  { value: 'verbatim', icon: 'fa-lock', labelKey: 'verbatim', descKey: 'verbatimDesc' },
  { value: 'adapt', icon: 'fa-sliders-h', labelKey: 'adapt', descKey: 'adaptDesc' },
  { value: 'rethink', icon: 'fa-lightbulb', labelKey: 'rethink', descKey: 'rethinkDesc' },
];

const LENGTH_OPTIONS = [
  { value: 'match', icon: 'fa-equals', labelKey: 'lengthMatch' },
  { value: 'shorter', icon: 'fa-compress-alt', labelKey: 'lengthShorter' },
  { value: 'longer', icon: 'fa-expand-alt', labelKey: 'lengthLonger' },
];

/**
 * Settings modal for deriving sibling variants from one existing variant.
 *
 * Similarity and brace policy are two separate choices on purpose — see the
 * module comment in variantForge.js for why one slider cannot cover both.
 *
 * @param {Object} props
 * @param {string} props.sourceText the variant being derived from
 * @param {number} props.sourceNumber its 1-based position, for the header
 * @param {string} [props.language] 'German' | 'English' — drives the labels
 * @param {number} [props.creditsPerVariant] shown as a running estimate
 * @param {(config: Object) => void} props.onGenerate
 * @param {() => void} props.onClose
 */
const VariantForgeModal = ({
  sourceText,
  sourceNumber,
  language = 'German',
  creditsPerVariant = 0,
  onGenerate,
  onClose,
}) => {
  const [config, setConfig] = useState({ ...DEFAULT_FORGE_CONFIG });
  const t = LABELS[language] || LABELS.English;

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const set = (patch) => setConfig(prev => ({ ...prev, ...patch }));

  const sourceWords = countTemplateWords(sourceText);
  const sourceInstructions = countTemplateInstructions(sourceText);
  const estimate = Math.round(creditsPerVariant * config.count * 100) / 100;

  const renderCardGroup = (options, activeValue, key) => (
    <div className="vforge-cards">
      {options.map(opt => {
        const active = activeValue === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            className={`vforge-card ${active ? 'active' : ''}`}
            onClick={() => set({ [key]: opt.value })}
          >
            <i className={`fa ${opt.icon} vforge-card-icon`} aria-hidden></i>
            <span className="vforge-card-text">
              <span className="vforge-card-label">{t[opt.labelKey]}</span>
              <span className="vforge-card-desc">{t[opt.descKey]}</span>
            </span>
            {active && <i className="fa fa-check vforge-card-check" aria-hidden></i>}
          </button>
        );
      })}
    </div>
  );

  return createPortal(
    <div className="vforge-overlay bcScrim" onClick={onClose}>
      <div className="vforge-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="vforge-header">
          <div className="vforge-header-text">
            <h3>{t.title}</h3>
            <p>{t.subtitle}</p>
          </div>
          <button type="button" className="vforge-close" onClick={onClose} aria-label={t.cancel}>
            <i className="fa fa-times" aria-hidden></i>
          </button>
        </div>

        <div className="vforge-source">
          <div className="vforge-source-head">
            <span className="vforge-source-badge">
              <i className="fa fa-bookmark" aria-hidden></i>
              {t.sourceLabel} · Variant {sourceNumber}
            </span>
            <span className="vforge-source-meta">
              {sourceWords} {t.words} · {sourceInstructions} {t.instructions}
            </span>
          </div>
          <p className="vforge-source-text">{sourceText}</p>
        </div>

        <div className="vforge-body">
          <div className="vforge-field">
            <label className="vforge-label">
              <i className="fa fa-fingerprint" aria-hidden></i> {t.similarity}
            </label>
            <p className="vforge-hint">{t.similarityHint}</p>
            {renderCardGroup(SIMILARITY_OPTIONS, config.similarity, 'similarity')}
          </div>

          <div className="vforge-divider" />

          <div className="vforge-field">
            <label className="vforge-label">
              <i className="fa fa-code" aria-hidden></i> {t.brace}
            </label>
            <p className="vforge-hint">{t.braceHint}</p>
            {renderCardGroup(BRACE_OPTIONS, config.bracePolicy, 'bracePolicy')}
            {sourceInstructions === 0 && (
              <p className="vforge-note-empty">
                <i className="fa fa-info-circle" aria-hidden></i> {t.braceEmpty}
              </p>
            )}
          </div>

          <div className="vforge-divider" />

          <div className="vforge-row">
            <div className="vforge-field vforge-half">
              <label className="vforge-label">
                <i className="fa fa-ruler-horizontal" aria-hidden></i> {t.length}
              </label>
              <div className="vforge-segmented">
                {LENGTH_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`vforge-seg-btn ${config.lengthMode === opt.value ? 'active' : ''}`}
                    onClick={() => set({ lengthMode: opt.value })}
                  >
                    <i className={`fa ${opt.icon}`} aria-hidden></i>
                    <span>{t[opt.labelKey]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="vforge-field vforge-half">
              <label className="vforge-label">
                <i className="fa fa-layer-group" aria-hidden></i> {t.count}
              </label>
              <div className="vforge-segmented">
                {Array.from({ length: MAX_FORGE_VARIANTS }, (_, i) => i + 1).map(n => (
                  <button
                    key={n}
                    type="button"
                    className={`vforge-seg-btn ${config.count === n ? 'active' : ''}`}
                    onClick={() => set({ count: n })}
                  >
                    <span>{n}</span>
                  </button>
                ))}
              </div>
              {creditsPerVariant > 0 && (
                <p className="vforge-cost">
                  <i className="fa fa-coins" aria-hidden></i> {t.costEstimate}: ~{estimate} {t.credits}
                  {config.count > 1 && ` (${creditsPerVariant} × ${config.count})`}
                </p>
              )}
            </div>
          </div>

          <div
            className="vforge-toggle-row"
            onClick={() => set({ keepFormatting: !config.keepFormatting })}
            role="switch"
            aria-checked={config.keepFormatting}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                set({ keepFormatting: !config.keepFormatting });
              }
            }}
          >
            <div className={`vforge-switch ${config.keepFormatting ? 'on' : ''}`}>
              <div className="vforge-switch-knob" />
            </div>
            <div className="vforge-toggle-text">
              <span className="vforge-toggle-label">
                <i className="fa fa-align-left" aria-hidden></i> {t.keepFormatting}
              </span>
              <span className="vforge-toggle-hint">{t.keepFormattingHint}</span>
            </div>
          </div>

          <div className="vforge-field">
            <label className="vforge-label">
              <i className="fa fa-comment-dots" aria-hidden></i> {t.note}
              <span className="vforge-optional">{t.noteOptional}</span>
            </label>
            <textarea
              className="vforge-textarea"
              value={config.note}
              onChange={(e) => set({ note: e.target.value })}
              placeholder={t.notePlaceholder}
              rows={2}
            />
            <p className="vforge-hint">{t.noteHint}</p>
          </div>
        </div>

        <div className="vforge-footer">
          <button type="button" className="vforge-cancel" onClick={onClose}>
            {t.cancel}
          </button>
          <button
            type="button"
            className="vforge-generate"
            onClick={() => onGenerate(normalizeForgeConfig(config))}
          >
            <i className="fa fa-magic" aria-hidden></i>
            <span>
              {config.count > 1 ? `${config.count} ${t.generateMany}` : t.generateOne}
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default VariantForgeModal;
